import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Download, PencilLine } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PageContainer } from '../components/layout/PageContainer';
import { Logo } from '../components/common/Logo';
import { Button } from '../components/common/Button';
import { AvatarPicker } from '../components/common/AvatarPicker';
import { api } from '../services/api';
import { redirectTarget } from '../utils/redirect';

/**
 * First-run profile setup.
 *
 * Reached whenever a signed-in subscriber still has no name — magic-link and
 * Google signups arrive with one missing, and standings filter out nameless
 * players, so the app needs it before they can appear anywhere.
 *
 * Two ways through: import the LetzPlay profile (handing off to /connect,
 * which already owns that flow) or fill it in by hand. Only the name is
 * required; photo, birth date and CPF are offered here because paid rankings
 * need the last two, and asking once beats interrupting a join later.
 */

/** "12345678909" → "123.456.789-09" while typing. */
function maskCpf(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscriber, refreshSubscriber } = useAuth();

  const target = redirectTarget(searchParams);
  // /connect sends people back here when they abandon the import.
  const [mode, setMode] = useState<'choice' | 'manual'>(
    searchParams.get('manual') === '1' ? 'manual' : 'choice'
  );

  const [name, setName] = useState(subscriber?.name || '');
  const [birthDate, setBirthDate] = useState(subscriber?.birthDate || '');
  const [cpf, setCpf] = useState(subscriber?.cpf ? maskCpf(subscriber.cpf) : '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleImport = () => {
    navigate(`/connect?redirect=${encodeURIComponent(target)}`);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setError('Digite seu nome');
      return;
    }

    const bareCpf = cpf.replace(/\D/g, '');
    if (bareCpf && bareCpf.length !== 11) {
      setError('CPF incompleto');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const response = await api.auth.updateProfile({
        name: trimmed,
        ...(bareCpf ? { cpf: bareCpf } : {}),
        ...(birthDate ? { birthDate } : {}),
      });
      if (!response.success) {
        setError(response.error?.message || 'Não foi possível salvar');
        return;
      }
      await refreshSubscriber();
      navigate(target, { replace: true });
    } catch {
      setError('Erro de conexão');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageContainer className="flex items-center justify-center min-h-screen px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Logo size="lg" className="mb-2" />
        </div>

        <div className="glass-card rounded-3xl p-6 sm:p-8">
          {mode === 'choice' ? (
            <>
              <div className="text-center mb-6">
                <h1 className="font-display text-xl font-bold text-white mb-2">
                  Vamos montar seu perfil
                </h1>
                <p className="text-white/60 text-sm">
                  Seu nome aparece na classificação dos rankings. Dá pra trazer da
                  plataforma de torneios ou preencher aqui.
                </p>
              </div>

              <div className="space-y-3">
                <Button onClick={handleImport} fullWidth>
                  <Download className="w-5 h-5" />
                  <span>Importar meu perfil</span>
                </Button>
                <Button onClick={() => setMode('manual')} variant="secondary" fullWidth>
                  <PencilLine className="w-5 h-5" />
                  <span>Preencher manualmente</span>
                </Button>
              </div>

              <p className="mt-5 text-center text-xs text-white/40">
                Importar traz só o seu nome e a sua foto.
              </p>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <h1 className="font-display text-xl font-bold text-white mb-2">
                  Seu perfil
                </h1>
                <p className="text-white/60 text-sm">
                  Só o nome é obrigatório — o resto dá pra completar depois.
                </p>
              </div>

              <div className="flex justify-center mb-6">
                <AvatarPicker size="lg" hint="Toque para adicionar uma foto" />
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="onboarding-name"
                    className="block text-xs text-white/50 mb-1.5"
                  >
                    Como você quer ser chamado? *
                  </label>
                  <input
                    id="onboarding-name"
                    type="text"
                    autoFocus
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (error) setError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSave();
                    }}
                    placeholder="Seu nome"
                    maxLength={60}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-turquoise/50"
                  />
                </div>

                <div>
                  <label
                    htmlFor="onboarding-birth"
                    className="block text-xs text-white/50 mb-1.5"
                  >
                    Data de nascimento
                  </label>
                  <input
                    id="onboarding-birth"
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    className="w-full min-w-0 min-h-[46px] appearance-none px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm text-left focus:outline-none focus:border-turquoise/50 [color-scheme:dark] [&::-webkit-date-and-time-value]:text-left"
                  />
                </div>

                <div>
                  <label htmlFor="onboarding-cpf" className="block text-xs text-white/50 mb-1.5">
                    CPF
                  </label>
                  <input
                    id="onboarding-cpf"
                    type="text"
                    inputMode="numeric"
                    value={cpf}
                    onChange={(e) => setCpf(maskCpf(e.target.value))}
                    placeholder="000.000.000-00"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-turquoise/50"
                  />
                  <p className="mt-1.5 text-[11px] text-white/30 leading-snug">
                    Usados apenas para gerar cobranças PIX ao entrar em rankings pagos.
                  </p>
                </div>

                {error && <p className="text-coral text-sm">{error}</p>}

                <Button onClick={handleSave} fullWidth isLoading={isSaving}>
                  <span>Continuar</span>
                </Button>

                <button
                  type="button"
                  onClick={() => setMode('choice')}
                  className="w-full text-center py-2 text-sm text-white/50 hover:text-white transition-colors"
                >
                  Voltar
                </button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </PageContainer>
  );
}
